import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { formatAuthError } from '../utils/authErrors';
import '../styles.css';

const quotes = [
  { text: 'Tires, Cole, is what wins a race.', author: 'Harry Hogge (Days of Thunder)' },
  { text: 'To race is to live, all the rest is simply waiting', author: 'Steve McQueen' },
  { text: "If you're in control, you're not going fast enough.", author: 'Parnelli Jones' },
  { text: 'Straight roads are for fast cars, turns are for fast drivers.', author: 'Colin McRae' },
  { text: 'If you no longer go for a gap that exists, you are no longer a racing driver', author: 'Ayrton Senna' },
  { text: "What's behind you doesn't matter", author: 'Enzo Ferrari' },
  { text: "It's marvelous to go very fast", author: 'Lisa (Grand Prix 1966)' },
  { text: "Just leave me alone, I know what to do", author: 'Kimi Räikkönen' },
  { text: "Auto racing, bull fighting, and mountain climbing are the only real sports… all the others are games.", author: 'Ernest Hemingway' },
  { text: "Faster, faster, faster, until the thrill of speed overcomes the fear of death.", author: 'Hunter Thompson' },
  { text: "Auto racing began five minutes after the second car was built.", author: 'Henry Ford' },
  { text: "To achieve anything in this game you must be prepare to dabble in the boundary of disaster.", author: 'Sterling Moss' },
];

function Register() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [randomQuote, setRandomQuote] = useState({ text: '', author: '' });

  useEffect(() => {
    const quote = quotes[Math.floor(Math.random() * quotes.length)];
    setRandomQuote(quote);
  }, []);

  const createMosportCircuit = async (userId) => {
    try {
      // Check if Mosport already exists in user's circuits
      const userCircuitsQuery = query(collection(db, `users/${userId}/circuits`), where('name', '==', 'Mosport'));
      const userCircuitsSnap = await getDocs(userCircuitsQuery);
      if (!userCircuitsSnap.empty) return; // Already exists

      // Fetch Mosport from global circuits collection
      const globalCircuitsQuery = query(collection(db, 'circuits'), where('name', '==', 'Mosport'));
      const globalCircuitsSnap = await getDocs(globalCircuitsQuery);
      if (globalCircuitsSnap.empty) return; // Silently fail if not found

      // Copy the global Mosport to user's circuits
      const globalCircuit = globalCircuitsSnap.docs[0].data();
      const circuitId = Date.now().toString();
      const circuitData = {
        ...globalCircuit,
        id: circuitId,
        timestamp: Date.now()
      };
      await setDoc(doc(db, `users/${userId}/circuits/${circuitId}`), circuitData);
    } catch (error) {
      // Silently fail - not critical for user registration
      console.warn('Failed to create Mosport circuit:', error);
    }
  };

  const handleRegister = async () => {
    setError(null);
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // Set trial status - 30 days free Pro
      const accessEndDate = new Date();
      accessEndDate.setDate(accessEndDate.getDate() + 30);
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        accessEndDate: accessEndDate.toISOString(),
        subscriptionStatus: 'trial',
        plan: 'pro', // Track that they started with Pro trial
        isAdmin: false,
        welcomeEmailSent: false // Flag for tracking welcome email status
      }, { merge: true });
      // Add Mosport circuit
      await createMosportCircuit(userCredential.user.uid);

      // Send welcome email (you'll need to implement this securely)
      // Note: This would require a backend service to avoid exposing credentials
      // For now, you'll need to manually trigger the email script

      navigate('/app');
    } catch (err) {
      setError(formatAuthError(err));
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      // Check if new user
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      if (!userDoc.exists()) {
        // New user, set trial
        const accessEndDate = new Date();
        accessEndDate.setDate(accessEndDate.getDate() + 30);
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          accessEndDate: accessEndDate.toISOString(),
          subscriptionStatus: 'trial',
          plan: 'pro',
          isAdmin: false,
          welcomeEmailSent: false // Flag for tracking welcome email status
        });
        // Add Mosport circuit
        await createMosportCircuit(userCredential.user.uid);
        navigate('/app');
      } else {
        // Existing user, redirect to app
        navigate('/app');
      }
    } catch (err) {
      setError(formatAuthError(err));
    }
  };

  return (
    <div className="login-container">
      <img src="/logo.png" alt="Logo" className="login-logo" />
      <div className="login-content">
        <h1 className="login-title">Start Your Free Trial</h1>
        <div className="trial-info" style={{ textAlign: 'center', marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#2563eb' }}>30 Days Free - Experience Pro</h3>
          <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
            Full access to all Pro features for 30 days. After your trial, choose between Enthusiast ($7/month) or Pro ($21/month) plans.
          </p>
        </div>
        <p className="login-quote" data-author={randomQuote.author}>{randomQuote.text}</p>
        {error && <p className="login-error">{error}</p>}
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Email"
          className="login-input"
        />
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Password (min 6 characters)"
          className="login-input"
        />
        <input
          type="password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          placeholder="Confirm Password"
          className="login-input"
        />
        <button onClick={handleRegister} className="btn btn-save">
          Create Account & Start Free Trial
        </button>
        <div style={{ textAlign: 'center', margin: '15px 0', color: '#666', fontSize: '14px' }}>or</div>
        <button onClick={handleGoogleSignIn} className="btn btn-google" style={{
          backgroundColor: '#fff',
          color: '#333',
          border: '1px solid #ddd',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          fontWeight: '500'
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>
        <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: '#666' }}>
          Already have an account? <a href="/login" style={{ color: '#2563eb', textDecoration: 'none' }}>Log in</a>
        </p>
      </div>
    </div>
  );
}

export default Register;